// Help & support page. Linked from the bottom of the sidebar (always visible, every view)
// and routed at /help for every signed-in user (routes/AppRoutes.jsx).
// [UPDATED] One "Contact support" card (e-mail fallback), a support request form, and the FAQ.
import { useMemo, useState } from "react";
import { FiMail, FiSearch, FiSend } from "react-icons/fi";
import { Card, PageHeader, SearchInput, Button, FormField } from "../../components/common";
import Select from "../../components/common/Select";
import { useRole } from "../../context/RoleContext";
import { VIEW_LABELS } from "../../services/navigation";
import { MAX_GOALS_PER_YEAR } from "../../utils/constants";
import api from "../../services/api";
import AccessMatrix from "../../components/common/AccessMatrix";

// PMS support mailbox (same address the backend sends e-mails from, GRAPH_SENDER in backend/.env).
export const SUPPORT_EMAIL = "pms.support@teliolabs.com";

// Support request form settings.
const CATEGORIES = ["Goals", "Ratings", "Deadlines", "Access / login", "Other"];
const MAX_SUBJECT = 120;
const MAX_MESSAGE = 2000;

// Questions and answers shown on the page. Add, edit or remove { q, a } entries here.
const FAQ = [
  {
    q: "What is the difference between a KRA and a goal?",
    a: "A KRA is a daily task the associate carries out in the role (for example: update tickets every day); KPIs measure it. A goal is a target given to the employee by the manager for the cycle (for example: automate the release checklist by March). Both are on the appraisal and both count in the score.",
  },
  {
    q: "What dates does the performance cycle cover?",
    a: "The current cycle runs from 1 October 2026 to 31 March 2027. Goal setting, self assessment, manager review and HR finalization each open on their own dates inside that period (see the stage tracker on your dashboard).",
  },
  {
    q: "I am a manager. Why is an organization-wide KRA marked Read only?",
    a: "Organization-wide KRAs, KPIs and templates are maintained by HR. Managers can use them, and can create and edit their own team KRAs, KPIs and templates. Use Duplicate on a template to start your own team version.",
  },
  {
    q: "What is my username?",
    a: "Your username is the one in your invitation e-mail (often the first part of your official e-mail). Sign in with the username, not the full e-mail address.",
  },
  {
    q: "I forgot my password. What do I do?",
    a: "On the sign-in page click \"Forgot Password?\" and enter your username. You get an e-mail with a link; open it and choose a new password (8 to 128 characters with uppercase, lowercase, a digit, and a special character; no spaces). The link works once and for a short time.",
  },
  {
    q: "My invitation or reset link says it is invalid or expired.",
    a: "Each link works only once and only for a limited time. For a reset link, request a new one from the sign-in page. For an invitation link, ask HR to send the invitation again.",
  },
  {
    q: "I lost my typing because the power went off / I closed the page.",
    a: "Most forms now keep a draft on your computer while you type. Open the same form again: your values come back with the message \"Your unsaved changes were restored\". Check them and click Save. Use \"Discard changes\" to start again. Drafts are deleted when you save, after 7 days, or when you sign out.",
  },
  {
    q: `Why can't I add another goal?`,
    a: `A year can have at most ${MAX_GOALS_PER_YEAR} goals (you create all your goals yourself; your manager approves them). Edit or remove a goal instead. The goal percentages on your sheet must also add up to 100%.`,
  },
  {
    q: "How do I change a goal after it was approved?",
    a: "Open the goal and request a change. Your manager approves or rejects the request. If it is approved, the sheet goes back to draft so you can edit it and submit it again.",
  },
  {
    q: "My progress update shows \"waiting for approval\".",
    a: "In some cycles your manager must approve each progress update. The goal keeps the old value until it is approved. If it is returned, read the comment, update it and save again.",
  },
  {
    q: "The self assessment or review button is disabled.",
    a: "Each stage is open only between its start and end dates (see the stage tracker on your dashboard). If the date has passed and you still need time, ask HR to extend the deadline for you.",
  },
  {
    q: "When can I see my final rating?",
    a: "After your manager's assessment, HR finalizes and releases the result. You then see it under My results and are asked to acknowledge it.",
  },
  {
    q: "An employee shows no reporting manager (HR).",
    a: "If an import named a manager that matched nobody or more than one person, the Employees page shows a yellow warning and a \"Needs review\" tag in the Reports to column. Click Review (or the tag), then choose the right manager or record that there is none.",
  },
  {
    q: "Which browser should I use?",
    a: "A recent version of Chrome, Edge or Firefox. If a page looks wrong, refresh it (Ctrl+F5) or sign out and sign in again.",
  },
];

// Opens a new e-mail to support in the user's e-mail app, with the user's details already filled in,
// so support knows who is writing and from where. Used as a fallback when the form cannot be used.
function supportMailto({ name, employeeId, view }) {
  const subject = "PMS support request";
  const body = [
    "Please write your question or describe the problem:",
    "",
    "",
    "----- added automatically -----",
    `Name: ${name || ""}`,
    `Employee ID: ${employeeId || ""}`,
    `View: ${view || ""}`,
    `Time: ${new Date().toLocaleString()}`,
    `Browser: ${navigator.userAgent}`,
  ].join("\n");
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// Support request form. The API derives the employee identity from the Bearer session.
function SupportForm({ profile, view }) {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === "sending") return;
    if (subject.trim().length < 3) return setError("Please enter a short subject.");
    if (message.trim().length < 10) return setError("Please describe the problem in a few words.");

    setError("");
    setStatus("sending");
    try {
      await api.post("/support", {
          category,
          subject: subject.trim(),
          message: message.trim(),
          view,
          browser: navigator.userAgent,
      });
      setSubject("");
      setMessage("");
      setCategory(CATEGORIES[0]);
      setStatus("sent");
    } catch {
      setStatus("idle");
      setError("Could not send your request. Please try again or use the e-mail button above.");
    }
  }

  if (status === "sent") {
    return (
      <div className="form-stack" role="status">
        <p>
          <strong>Your request was sent.</strong> Support will reply to your official e-mail.
        </p>
        <div>
          <Button variant="secondary" onClick={() => setStatus("idle")}>Send another request</Button>
        </div>
      </div>
    );
  }

  return (
    <form className="form-stack" onSubmit={handleSubmit} noValidate>
      <FormField label="Category" required>
        <Select className="form-control" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Select>
      </FormField>

      <FormField label="Subject" required>
        <input
          className="form-control"
          type="text"
          value={subject}
          maxLength={MAX_SUBJECT}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Short summary of the problem"
        />
      </FormField>

      <FormField label="Message" required hint={`${message.length}/${MAX_MESSAGE}`}>
        <textarea
          className="form-control"
          rows={5}
          value={message}
          maxLength={MAX_MESSAGE}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Describe what happened and what you expected"
        />
      </FormField>

      {error && <p className="form-error" role="alert">{error}</p>}

      <div>
        <Button type="submit" icon={FiSend} loading={status === "sending"}>
          {status === "sending" ? "Sending..." : "Send request"}
        </Button>
      </div>
    </form>
  );
}

export default function HelpSupportPage() {
  const { activeView, profile } = useRole();
  const [search, setSearch] = useState("");
  const viewLabel = VIEW_LABELS[activeView];

  const faqs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? FAQ.filter((f) => `${f.q} ${f.a}`.toLowerCase().includes(q)) : FAQ;
  }, [search]);

  return (
    <div className="page help-page">
      <PageHeader
        title="Help & support"
        subtitle="Answers to common questions, and how to contact PMS support."
      />

      <Card className="help-contact help-contact-single">
        <FiMail size={22} aria-hidden="true" />
        <div className="help-contact-text">
          <h2>Contact support</h2>
          <p className="muted">
            Use the form below for questions or problems with goals, ratings, deadlines or access. If the form
            does not work, you can e-mail support directly; your e-mail app opens with your name and employee ID
            already filled in.
          </p>
        </div>
        <a
          className="btn btn-primary"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          href={supportMailto({ name: profile?.name, employeeId: profile?.id, view: viewLabel })}
        >
          {/* Icon color is forced to the button text color so it is always visible, not only on hover. */}
          <FiMail size={16} aria-hidden="true" style={{ color: "currentColor", stroke: "currentColor", opacity: 1, flexShrink: 0 }} />
          <span>{SUPPORT_EMAIL}</span>
        </a>
      </Card>

      <Card
        title="Who can do what (KRA & KPI)"
        subtitle="What each role can do with KRAs (daily tasks), KPIs, templates and assignments. Your column is highlighted; its entries link to the screen."
        padded={false}
      >
        <AccessMatrix activeView={activeView} />
      </Card>

      <Card title="Send a support request">
        <SupportForm profile={profile} view={viewLabel} />
      </Card>

      <Card
        title="Frequently asked questions"
        actions={<SearchInput value={search} onChange={setSearch} placeholder="Search questions" />}
      >
        {faqs.length === 0 ? (
          <p className="muted"><FiSearch size={14} aria-hidden="true" /> No question matches. Try another word or contact support.</p>
        ) : (
          <div className="help-faq">
            {faqs.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
